<script lang="ts" setup>
import HELPER from '@/app/utils/HELPER';

//types
import type { Product } from '../type';

//composable
import { useProductCard } from '../composable/useProductCard';
import { usePickProduct } from '../composable/usePickProduct';
import { useProductPickerStore } from '../composable/useProductPickerStore';

//hooks
const { onPickProduct } = usePickProduct();
const { transformImgSrc } = useProductCard();
const { setOpenPopup, resetProductData } = useProductPickerStore();

//props
const props = defineProps<{ data: Product }>();

//computed
const productData = computed(() => {
  return props.data;
});

const imgSrc = computed(() => {
  return transformImgSrc(productData.value?.image);
});

const status = computed(() => {
  return HELPER.capitalizeFirstLetter(productData.value?.status?.toLowerCase());
});

const dynamicStatusClass = computed(() => {
  return `product-card__status-${productData.value?.status?.toLowerCase()}`;
});

//methods
const pickProduct = () => {
  setOpenPopup(false);
  onPickProduct(productData?.value);
  resetProductData();
};
</script>

<template>
  <div
    class="product-card group d-flex align-items-center justify-content-start clickable mb-[12px] h-[72px] rounded-[3px] border-[1px] border-solid border-[#eeeeee] bg-white transition-all last:mb-0 hover:border-[#494949]"
    @click.stop="pickProduct">
    <div class="grow-1 d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <div class="mr-[12px] h-[70px] w-[72px] rounded-l-[3px]">
          <img :src="imgSrc" alt="product-img" class="h-full w-full rounded-l-[3px] object-cover" loading="lazy" />
        </div>
        <div class="product-card__content">
          <div
            v-popover:tooltip.top="productData.title"
            class="product-card__title body-12-medium text-light-high max-w-[300px] overflow-hidden"
            @click.stop="pickProduct">
            {{ productData.title }}
          </div>
        </div>
      </div>
      <div class="product-card__body-right d-flex align-items-center justify-content-end col-md-3 col-xl-3">
        <div
          v-if="productData.status !== 'active'"
          class="paragraph-12-medium px-half h-[16px] w-fit cursor-auto rounded-[3px]"
          :class="dynamicStatusClass">
          {{ status }}
        </div>
        <div class="d-flex ml-3 h-[28px] w-[28px] items-center justify-center">
          <div
            class="h-[14px] w-[14px] rounded-full border-[2.5px] border-[#ffffff] bg-[#3C67FF] outline outline-[1.25px] outline-[#3C67FF]"
            :class="productData.isPicked ? 'visible' : 'invisible'"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/core/themes/vendor/_color.scss';

.product-card__title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.product-card__status {
  &-archived {
    color: $text-light-high;
    background-color: $text-dark-high;
  }
  &-draft {
    color: map-get($accent-color-amber, '500');
    background-color: map-get($accent-color-amber, '100');
  }
}
</style>
